import { gql, TypedDocumentNode, useMutation } from '@apollo/client';
import { Nullable } from '@e2m-customer-portal/shared';
import { accountFragment } from './fragments';
import {
  Account,
  AssignedPermissionInput,
  UpdateAccountMutation,
  UpdateAccountMutationVariables,
} from '../../generated/graphql';

export const updateAccountMutation: TypedDocumentNode<UpdateAccountMutation, UpdateAccountMutationVariables> = gql`
  mutation UpdateAccount(
    $email: String!
    $name: String!
    $permissions: [AssignedPermissionInput!]!
    $marketLocationIds: [ID!]
  ) {
    updateAccount(email: $email, name: $name, permissions: $permissions, marketLocationIds: $marketLocationIds) {
      ...AccountFragment
    }
  }
  ${accountFragment}
`;

export type UpdateAccount = (
  email: string,
  name: string,
  permissions: readonly AssignedPermissionInput[],
  marketLocationIds?: Nullable<string[]>,
) => Promise<Account>;

export function useUpdateAccount(): UpdateAccount {
  const [update] = useMutation(updateAccountMutation);

  return async (email, name, permissions, marketLocationIds) => {
    const { data, errors } = await update({
      variables: {
        email,
        name,
        permissions: permissions.map((permission) => ({
          permissionId: permission.permissionId,
          granted: permission.granted,
        })),
        marketLocationIds,
      },
    });

    if (errors?.length) {
      throw errors[0];
    }

    if (!data) {
      // Should not happen: https://github.com/apollographql/apollo-client/issues/6374
      throw new Error('Expected data, nothing received.');
    }

    return data.updateAccount;
  };
}
